import { useCallback, useEffect, useState } from "react";
import { Layers, Undo2, Recycle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const sourceLabel = (r) =>
  r.origin === "opening_stock" || r.origin === "stock_backfill" ? "Opening stock" : "Dispatch";

// Studio-facing per-roll list: return a roll to HQ, or log standalone (non-job) wastage.
export const StudioRolls = ({ studioId }) => {
  const [rolls, setRolls] = useState([]);
  const [ret, setRet] = useState(null);
  const [waste, setWaste] = useState(null);
  const [form, setForm] = useState({ reason: "", sqft: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = studioId && studioId !== "all" ? `&studio_id=${studioId}` : "";
    api
      .get(`/roll-serials?status=received&has_balance=true${q}`)
      .then(({ data }) => setRolls(data || []))
      .catch(() => setRolls([]));
  }, [studioId]);

  useEffect(() => { load(); }, [load]);

  const submitReturn = async () => {
    setBusy(true);
    try {
      await api.post(`/roll-serials/${ret.id}/return`, { reason: form.reason });
      toast.success(`Return requested for ${ret.serial} — awaiting HQ`);
      setRet(null); setForm({ reason: "", sqft: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitWastage = async () => {
    setBusy(true);
    try {
      await api.post(`/roll-serials/${waste.id}/wastage`, {
        sqft: Number(form.sqft || 0), reason: form.reason,
      });
      toast.success(`Wastage logged on ${waste.serial}`);
      setWaste(null); setForm({ reason: "", sqft: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!rolls.length) return null;

  return (
    <Card className="mb-6 border-brand-line shadow-none" data-testid="studio-rolls">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Layers className="h-4 w-4 text-brand" /> Your PPF / Sunfilm rolls
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="p-2.5">Serial</th>
              <th className="p-2.5">Material</th>
              <th className="p-2.5">Source</th>
              <th className="p-2.5 text-right">Remaining</th>
              <th className="p-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rolls.map((r) => (
              <tr key={r.id} className="border-t border-brand-line" data-testid={`studio-roll-${r.serial}`}>
                <td className="p-2.5 font-mono">{r.serial}{r.is_partial ? " · Partial" : ""}</td>
                <td className="p-2.5">{r.product_type}</td>
                <td className="p-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${sourceLabel(r) === "Opening stock" ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"}`}>
                    {sourceLabel(r)}
                  </span>
                </td>
                <td className="p-2.5 text-right font-medium">{Math.round(r.balance_sqft)} sqft</td>
                <td className="p-2.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="h-8"
                      data-testid={`roll-return-${r.serial}`}
                      onClick={() => { setRet(r); setForm({ reason: "", sqft: "" }); }}>
                      <Undo2 className="mr-1 h-3.5 w-3.5" /> Return
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 border-amber-200 text-amber-700 hover:bg-amber-50"
                      data-testid={`roll-wastage-${r.serial}`}
                      onClick={() => { setWaste(r); setForm({ reason: "", sqft: "" }); }}>
                      <Recycle className="mr-1 h-3.5 w-3.5" /> Wastage
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={!!ret} onOpenChange={(o) => !o && setRet(null)}>
        <DialogContent className="sm:max-w-md" data-testid="roll-return-dialog">
          <DialogHeader>
            <DialogTitle>Return roll {ret?.serial} to HQ</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-500">
            {Math.round(ret?.balance_sqft || 0)} sqft remaining will go back to HQ with the same
            serial number. The roll is locked until HQ confirms.
          </DialogDescription>
          <div>
            <Label>Reason for return *</Label>
            <Input className="mt-1.5" data-testid="roll-return-reason" value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRet(null)}>Cancel</Button>
            <Button data-testid="roll-return-submit" disabled={busy || !form.reason.trim()} onClick={submitReturn}>
              Request return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!waste} onOpenChange={(o) => !o && setWaste(null)}>
        <DialogContent className="sm:max-w-md" data-testid="roll-wastage-dialog">
          <DialogHeader>
            <DialogTitle>Log wastage — {waste?.serial}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-500">
            {Math.round(waste?.balance_sqft || 0)} sqft remaining. Wastage is separate from job
            consumption and is deducted from this roll.
          </DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sqft wasted *</Label>
              <Input className="mt-1.5" inputMode="decimal" data-testid="roll-wastage-sqft" value={form.sqft}
                onChange={(e) => setForm((f) => ({ ...f, sqft: e.target.value }))} />
            </div>
            <div>
              <Label>Reason *</Label>
              <Input className="mt-1.5" data-testid="roll-wastage-reason" value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaste(null)}>Cancel</Button>
            <Button data-testid="roll-wastage-submit"
              disabled={busy || !form.reason.trim() || !(Number(form.sqft) > 0)}
              onClick={submitWastage}>
              Log wastage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StudioRolls;
